import { useState, Fragment } from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { ShieldCheckIcon, MailIcon } from 'lucide-react';
import { useMutation } from '@apollo/client/react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import { AppCriticalityEnum } from 'src/graphql/schema';
import { AppCreateDocument } from 'src/graphql/actions/apps/appCreate.mutation';
import { AppRequestCreateDocument } from 'src/graphql/actions/appRequests/appRequestCreate.mutation';
import { getApiError, ClientError } from 'src/utils/errors';
import { Button } from '@/components/ui/button';
import { useAppLogoUpload, type IOwnApplicationForm } from 'src/containers/Application/model';
import { OwnApplicationForm } from 'src/containers/Application/ui';
import { refetchApps } from 'src/containers/Applications/List/api';
import { refetchAppRequests } from 'src/containers/VendorRequests/api';

import { type ApplicationType, type IVendorRequestForm } from '../model';

import AppTypeCard from './AppTypeCard';
import VendorRequestForm from './VendorRequestForm';

const AddApplicationContainer = () => {
  const navigate = useNavigate();
  const [createApp] = useMutation(AppCreateDocument);
  const [createAppRequest] = useMutation(AppRequestCreateDocument);
  const uploadAppLogo = useAppLogoUpload();
  const [selectedType, setSelectedType] = useState<ApplicationType | null>(null);

  const handleTypeChange = (value: string) => setSelectedType(value as ApplicationType);

  const handleOwnApplicationSubmit = async (data: IOwnApplicationForm) => {
    try {
      const logo = data.logo instanceof File ? await uploadAppLogo(data.logo) : undefined;

      const { data: result } = await createApp({
        variables: {
          appParams: {
            name: data.name,
            kind: data.kind,
            includedApps: data.includedApps,
            criticality: data.criticality as AppCriticalityEnum,
            industry: data.industry,
            softwareType: data.softwareType,
            jurisdiction: data.jurisdiction,
            description: data.description,
            logo,
          },
        },
      });

      if (result?.appCreate?.success) {
        toast.success(`Application ${result.appCreate.app?.name} created`);
        refetchApps();
        navigate('/applications');
      } else {
        throw new ClientError(result?.appCreate?.errors?.[0]);
      }
    } catch (error) {
      getApiError(error, toast.error);
    }
  };

  const handleVendorRequestSubmit = async (data: IVendorRequestForm) => {
    try {
      const { data: result } = await createAppRequest({
        variables: {
          appRequestParams: data,
        },
      });

      if (result?.appRequestCreate?.success) {
        toast.success(`Request sent to ${result.appRequestCreate.appRequest?.vendorName}`);
        refetchAppRequests();
        navigate('/applications');
      } else {
        throw new ClientError(result?.appRequestCreate?.errors?.[0]);
      }
    } catch (error) {
      getApiError(error, toast.error);
    }
  };

  const handleCancel = () => navigate('/applications');

  const setInitialSelectedType = () => setSelectedType(null);

  return (
    <Fragment>
      <RadioGroupPrimitive.Root
        aria-label="Application type"
        value={selectedType}
        onValueChange={handleTypeChange}
        className="grid grid-cols-1 gap-4 sm:grid-cols-2"
      >
        <AppTypeCard
          value="own"
          icon={ShieldCheckIcon}
          title="Create an application you own"
          description="Software you build yourself. You set it up and choose the products that protect it."
        />
        <AppTypeCard
          value="vendor"
          icon={MailIcon}
          title="Request a vendor's application"
          description="Software you rely on from a vendor. You invite them, and they set it up themselves."
        />
      </RadioGroupPrimitive.Root>
      {selectedType === 'own' && <OwnApplicationForm onSubmit={handleOwnApplicationSubmit} onCancel={handleCancel} />}
      {selectedType === 'vendor' && (
        <Fragment>
          <Button
            variant="link"
            size="sm"
            className="text-secondary-foreground font-bold p-0"
            onClick={setInitialSelectedType}
          >
            Adding your own application instead? Change type
          </Button>
          <VendorRequestForm onSubmit={handleVendorRequestSubmit} onCancel={handleCancel} />
        </Fragment>
      )}
    </Fragment>
  );
};

export default AddApplicationContainer;
